import styled, { createGlobalStyle } from 'styled-components';

/* ─── Global ──────────────────────────────────────────────────────────────── */

export const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700&display=swap');

  .simple-header {
    display: none !important;
  }

  /* Outlined Material-style inputs */
  input[type="email"],
  input[type="password"],
  input[type="text"] {
    width: 100%;
    box-sizing: border-box;
    border: 1.5px solid #61cbc9;
    border-radius: 8px;
    padding: 13px 14px;
    font-size: 15px;
    font-family: 'Roboto', sans-serif;
    color: #1a1a1a;
    background: #fff;
    outline: none;
    transition: border-color 0.2s;

    &::placeholder {
      color: #bbb;
    }

    &:focus {
      border-color: #61cbc9;
      box-shadow: 0 0 0 3px rgba(97, 203, 201, 0.15);
    }
  }

  /* Login button */
  button[type="submit"],
  button.login-btn {
    background-color: #61cbc9;
    color: #fff;
    padding: 12px 36px;
    font-size: 16px;
    font-weight: 600;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-family: 'Roboto', sans-serif;
    letter-spacing: 0.01em;
    transition: background-color 0.2s, transform 0.1s;
    white-space: nowrap;

    &:hover {
      background-color: #4db8b6;
    }

    &:active {
      background-color: #3aa5a3;
      transform: scale(0.99);
    }
  }

  /* Checkbox */
  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    accent-color: #61cbc9;
    cursor: pointer;
  }
`;

/* ─── Layout ──────────────────────────────────────────────────────────────── */

/**
 * Full-viewport container with the soft pastel gradient background
 * matching the screenshot (light lavender → warm white → soft pink/yellow).
 */
export const SuperAdminContainer = styled.div`
  display: flex;
  width: 100vw;
  height: 100dvh;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  /* Pastel gradient — matches the screenshot background */
  background: radial-gradient(ellipse at 0% 0%, #dde3f0 0%, transparent 55%),
              radial-gradient(ellipse at 100% 100%, #f5d6e8 0%, transparent 55%),
              radial-gradient(ellipse at 80% 10%, #fef0c7 0%, transparent 45%),
              #f0f2f8;

  @media (max-width: 480px) {
    align-items: flex-start;
    padding-top: 40px;
    overflow-y: auto;
  }
`;

/* White card — centred, same shadow as LoginPage */
export const CenterCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 48px 52px 40px;
  width: 100%;
  max-width: 660px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.10);
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 720px) {
    max-width: 92vw;
    padding: 40px 28px 32px;
  }

  @media (max-width: 480px) {
    padding: 32px 20px 28px;
  }
`;

/* ─── Typography ──────────────────────────────────────────────────────────── */

export const Title = styled.h1`
  font-family: 'Roboto', sans-serif;
  font-size: 1.25rem;
  font-weight: 500;
  color: #1a1a1a;
  margin: 0 0 4px;
  text-align: center;
  line-height: 1.4;
`;

export const Subtitle = styled.p`
  font-size: 14px;
  color: #888;
  text-align: center;
  margin: 0 0 32px;
`;

/* ─── Form ────────────────────────────────────────────────────────────────── */

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 0;
`;

export const InputGroup = styled.div`
  position: relative;
  margin-bottom: 24px;
  width: 100%;
`;

/**
 * Floating label that sits on the top border of the input,
 * matching the Material outlined style in the screenshot.
 */
export const InputLabel = styled.label`
  position: absolute;
  top: -10px;
  left: 12px;
  font-size: 12px;
  color: #61cbc9;
  background: #fff;
  padding: 0 4px;
  z-index: 1;
  font-family: 'Roboto', sans-serif;
  font-weight: 400;
  line-height: 1;
`;

export const Star = styled.span`
  color: #e05c5c;
  padding-left: 1px;
`;

/* ─── Checkbox row ────────────────────────────────────────────────────────── */

export const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
`;

export const CheckboxLabel = styled.label`
  font-size: 14px;
  color: #555;
  line-height: 1;
  cursor: pointer;
  font-family: 'Roboto', sans-serif;
`;

/* ─── Eye icon ────────────────────────────────────────────────────────────── */

export const EyeIcon = styled.img`
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  cursor: pointer;
  opacity: 0.55;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

/* ─── Divider ─────────────────────────────────────────────────────────────── */

export const Divider = styled.hr`
  width: 100%;
  border: none;
  border-top: 1px solid #ebebeb;
  margin: 16px 0 0;
`;

/* ─── Error popup (identical to LoginPage) ────────────────────────────────── */

export const ErrorPopup = styled.div`
  position: fixed;
  top: 20px;
  right: -600px;
  background: rgba(255, 255, 255, 0.95);
  border-left: 4px solid #61cbc9;
  color: #333;
  padding: 14px 44px 14px 20px;
  border-radius: 8px;
  width: 90%;
  max-width: 380px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  z-index: 1000;
  font-size: 14px;
  transition: right 0.45s cubic-bezier(0.16, 1, 0.3, 1);

  ${({ show }) =>
    show &&
    `
    right: 20px;
  `}

  @media (max-width: 768px) {
    top: auto;
    bottom: 20px;
    left: 50%;
    right: auto;
    transform: translateX(-50%);
  }
`;

export const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 22px;
  color: #aaa;
  cursor: pointer;
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  line-height: 1;
  padding: 0;

  &:hover {
    color: #61cbc9;
  }
`;
